import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api, ApiError } from '../../api';
import { Loading, ErrorState, Empty, fmtDate } from '../../components/ui';
import { qtyOf } from './Dashboard';

/* ============================================================
   Surat jalan — daftar mentah.

   Kuantitas negatif dibiarkan tampil negatif. Retur itu nyata,
   barangnya benar-benar pergi dari site, dan menyembunyikan
   tandanya akan membuat material yang sudah ditarik kembali tetap
   terhitung tiba.
   ============================================================ */

export type ProcDelivery = {
  id: string;
  poId: string;
  poNo: string;
  packageId: string;
  packageCode: string;
  noteNo: string;
  deliveredOn: string;
  qty: number;
  unit: string;
  note: string | null;
  receivedBy: string | null;
};

export function Deliveries({ projectId }: { projectId: string }) {
  const nav = useNavigate();
  const [deliveries, setDeliveries] = useState<ProcDelivery[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');

  const load = useCallback(async () => {
    try {
      const r = await api.get<{ deliveries: ProcDelivery[] }>(
        `/proc/deliveries?projectId=${projectId}`,
      );
      setDeliveries(r.deliveries);
      setError(null);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Could not load deliveries.');
    }
  }, [projectId]);

  useEffect(() => {
    void load();
  }, [load]);

  if (error) return <ErrorState message={error} onRetry={load} />;
  if (!deliveries) return <Loading rows={4} />;

  const q = search.trim().toLowerCase();
  const shown = deliveries.filter((d) => {
    // Urutan leksikografis yyyy-mm-dd sama dengan kronologisnya, jadi
    // penyaring tanggal tidak perlu menyentuh Date sama sekali.
    if (from && d.deliveredOn < from) return false;
    if (to && d.deliveredOn > to) return false;
    if (!q) return true;
    return [d.noteNo, d.poNo, d.packageCode].some((v) => v.toLowerCase().includes(q));
  });

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Pengiriman</h1>
          <p>Satu baris satu surat jalan. Yang kurang tetap kurang sampai barangnya benar-benar tiba.</p>
        </div>
      </div>

      <div className="filters">
        <input
          className="input"
          placeholder="Cari nomor surat jalan, PO, atau paket"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <input
          className="input"
          type="date"
          title="Terkirim sejak"
          value={from}
          onChange={(e) => setFrom(e.target.value)}
        />
        <input
          className="input"
          type="date"
          title="Terkirim sampai"
          value={to}
          onChange={(e) => setTo(e.target.value)}
        />
      </div>

      {deliveries.length === 0 ? (
        <Empty
          title="Belum ada surat jalan"
          body="Surat jalan dicatat terhadap sebuah PO, jadi selama belum ada PO yang terbit belum ada apa pun yang bisa dikirim terhadapnya."
          action={
            <button className="btn btn-primary" onClick={() => nav('/packages')}>
              Buka daftar paket
            </button>
          }
        />
      ) : shown.length === 0 ? (
        <Empty title="Tidak ada surat jalan yang cocok" body="Longgarkan penyaringnya." />
      ) : (
        <>
          <div className="tbl-wrap">
            <table className="tbl">
              <thead>
                <tr>
                  <th>Surat jalan</th>
                  <th>Tanggal</th>
                  <th>PO</th>
                  <th>Paket</th>
                  <th>Kuantitas</th>
                  <th>Diterima oleh</th>
                  <th>Catatan</th>
                </tr>
              </thead>
              <tbody>
                {shown.map((d) => (
                  <tr key={d.id} onClick={() => nav(`/packages/${d.packageId}`)}>
                    <td className="num">{d.noteNo}</td>
                    <td>{fmtDate(d.deliveredOn)}</td>
                    <td>{d.poNo}</td>
                    <td>{d.packageCode}</td>
                    <td className={d.qty < 0 ? 'neg' : ''}>{qtyOf(d.qty, d.unit)}</td>
                    <td>{d.receivedBy ?? '—'}</td>
                    <td>{d.note ?? '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {/* Kuantitasnya sengaja tidak dijumlahkan: satuan tiap paket
              berbeda, dan satu angka gabungan dari ton dan meter tidak
              menjawab pertanyaan siapa pun. */}
          <p className="hint">
            {shown.length} dari {deliveries.length} surat jalan tampil.
          </p>
        </>
      )}
    </>
  );
}
